/**
 * Floating chat widget for the DocScout AI agent.
 * Three modes: collapsed (FAB), panel (floating), expanded (right sidebar).
 * Uses async submit+poll pattern with server-side session persistence.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, IconButton, Typography, TextField, Paper, Fab, Fade, Chip, CircularProgress, Tooltip,
  Button, List, ListItemButton, ListItemText,
} from '@mui/material';
import {
  SmartToy, Close, Remove, Send, AutoAwesome, Warning, OpenInFull, CloseFullscreen,
  Add, Replay, Delete, History,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { usePageContext } from '../../hooks/usePageContext';
import {
  AgentEvent, submitAgentMessage, pollAgentMessage, fetchSessions, fetchSessionMessages, deleteSession,
} from '../../services/agentApi';
import type { AgentSession } from '../../services/agentApi';
import { emitAgentDataChange } from '../../utils/agentEvents';

/** Theme-derived colors for inline HTML builders. */
interface MarkdownColors {
  border: string;
  headerBg: string;
  codeBg: string;
  hrColor: string;
}

/** Parse markdown tables into HTML with own escaping. */
function renderTables(text: string, colors: MarkdownColors): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return text.replace(
    /((?:^\|.+\|$\n?){2,})/gm,
    (block) => {
      const rows = block.trim().split('\n').filter(r => r.trim());
      if (rows.length < 2) return block;
      const isSep = (r: string) => /^\|[\s\-:|]+\|$/.test(r);
      const parseRow = (r: string) =>
        r.split('|').slice(1, -1).map(c => c.trim());
      const headerCells = parseRow(rows[0]);
      const dataRows = rows.slice(isSep(rows[1]) ? 2 : 1).filter(r => !isSep(r));
      const th = `padding:4px 8px;border:1px solid ${colors.border};background:${colors.headerBg};font-size:12px;white-space:nowrap`;
      const td = `padding:4px 8px;border:1px solid ${colors.border};font-size:12px;vertical-align:top`;
      let html = '<div style="overflow-x:auto;margin:8px 0"><table style="border-collapse:collapse;width:100%">';
      html += '<thead><tr>' + headerCells.map(c => `<th style="${th}">${esc(c)}</th>`).join('') + '</tr></thead>';
      html += '<tbody>' + dataRows.map(r =>
        '<tr>' + parseRow(r).map(c => `<td style="${td}">${esc(c)}</td>`).join('') + '</tr>'
      ).join('') + '</tbody></table></div>';
      return html;
    }
  );
}

/** Lightweight markdown to HTML renderer for assistant messages. */
function renderMarkdown(text: string, colors: MarkdownColors): string {
  const tables: string[] = [];
  text = renderTables(text, colors).replace(/<div style="overflow-x:auto[^]*?<\/table><\/div>/g, (m) => {
    tables.push(m);
    return `\x00TBL${tables.length - 1}\x00`;
  });
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<strong style="font-size:0.9em;display:block;margin:8px 0 4px">$1</strong>')
    .replace(/^## (.+)$/gm, '<strong style="font-size:1em;display:block;margin:10px 0 4px">$1</strong>')
    .replace(/^# (.+)$/gm, '<strong style="font-size:1.1em;display:block;margin:12px 0 4px">$1</strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, `<code style="background:${colors.codeBg};padding:1px 4px;border-radius:3px;font-size:0.85em">$1</code>`)
    .replace(/^---$/gm, `<hr style="border:none;border-top:1px solid ${colors.hrColor};margin:8px 0"/>`)
    .replace(/^- (.+)$/gm, '<div style="padding-left:12px">• $1</div>')
    .replace(/^\d+\. (.+)$/gm, (_, p1, offset, str) => {
      const before = str.substring(0, offset);
      const num = (before.match(/^\d+\. /gm) || []).length + 1;
      return `<div style="padding-left:12px">${num}. ${p1}</div>`;
    })
    .replace(/\n/g, '<br/>')
    .replace(/\x00TBL(\d+)\x00/g, (_, i) => tables[parseInt(i)]);
}

interface ChatMessage {
  role: 'user' | 'assistant' | 'tool';
  content: string;
  toolName?: string;
  toolStatus?: 'start' | 'done' | 'error';
}

type ViewMode = 'panel' | 'expanded';

const POLL_INTERVAL_MS = 2500;

/** Generate a UUID, with fallback for environments without crypto.randomUUID. */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export const ChatWidget: React.FC = () => {
  const theme = useTheme();
  const mdColors: MarkdownColors = {
    border: theme.palette.divider,
    headerBg: theme.palette.action.hover,
    codeBg: theme.palette.action.selected,
    hrColor: theme.palette.divider,
  };
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('panel');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>(generateUUID());
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [activeView, setActiveView] = useState<'sessions' | 'chat'>('chat');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pageContext = usePageContext();
  const pageContextRef = useRef(pageContext);
  pageContextRef.current = pageContext;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages, loading]);

  // Fetch sessions when panel opens; auto-switch to chat if none exist
  useEffect(() => {
    if (!open || activeView !== 'sessions') return;
    fetchSessions().then(data => {
      setSessions(data);
      if (data.length === 0) setActiveView('chat');
    }).catch(() => {
      setSessions([]);
      setActiveView('chat');
    });
  }, [open, activeView]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const eventsToMessages = useCallback((events: AgentEvent[]): ChatMessage[] => {
    const msgs: ChatMessage[] = [];
    for (const evt of events) {
      switch (evt.type) {
        case 'text':
          msgs.push({ role: 'assistant', content: evt.content || '' });
          break;
        case 'tool_start':
          msgs.push({
            role: 'tool', content: evt.summary || `Using ${evt.tool}...`,
            toolName: evt.tool, toolStatus: 'start',
          });
          break;
        case 'tool_done':
          msgs.push({
            role: 'tool', content: evt.summary || `✓ ${evt.tool} completed`,
            toolName: evt.tool, toolStatus: evt.summary?.startsWith('Error') ? 'error' : 'done',
          });
          break;
      }
    }
    return msgs;
  }, []);

  const processEvents = useCallback((events: AgentEvent[]) => {
    const newMessages = eventsToMessages(events);
    if (newMessages.length > 0) {
      setMessages(prev => [...prev, ...newMessages]);
    }
    for (const evt of events) {
      if (evt.type === 'tool_done' && evt.tool && !evt.summary?.startsWith('Error')) {
        emitAgentDataChange(evt.tool);
      }
    }
  }, [eventsToMessages]);

  // Poll for response when currentRequestId is set
  useEffect(() => {
    if (!currentRequestId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await pollAgentMessage(currentRequestId);
        if (cancelled) return;
        if (res.status === 'COMPLETED') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setCurrentRequestId(null);
          if (res.events) processEvents(res.events);
          setLoading(false);
        } else if (res.status === 'ERROR') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setCurrentRequestId(null);
          setError(res.errorMessage || 'Something went wrong.');
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: res.errorMessage || 'I encountered an error. Please try again.',
          }]);
          setLoading(false);
        }
        // PENDING/PROCESSING — keep polling, "Thinking..." shown via loading state
      } catch (e: any) {
        if (cancelled) return;
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setCurrentRequestId(null);
        setError(e.message || 'Polling failed.');
        setMessages(prev => [...prev, { role: 'assistant', content: 'I encountered an error. Please try again.' }]);
        setLoading(false);
      }
    };

    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    // Run first poll immediately
    poll();

    return () => {
      cancelled = true;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [currentRequestId, processEvents]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setInput('');
    setError(null);
    setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setLoading(true);

    try {
      const { requestId } = await submitAgentMessage(sessionId, trimmed, pageContextRef.current);
      setCurrentRequestId(requestId);
    } catch (e: any) {
      const errMsg = e.message || 'Something went wrong.';
      if (errMsg.includes('401') || errMsg.includes('Authentication')) {
        setError('Your session has expired. Please refresh the page to re-authenticate.');
        setMessages(prev => [...prev, { role: 'assistant', content: 'Your session has expired. Please refresh the page to log in again.' }]);
      } else {
        setError(errMsg);
        setMessages(prev => [...prev, { role: 'assistant', content: 'I encountered an error. Please try again.' }]);
      }
      setLoading(false);
    }
  };

  const handleRetry = () => {
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUserMsg) return;
    // Remove the last assistant error message before retrying
    setMessages(prev => {
      const last = prev[prev.length - 1];
      return last && last.role === 'assistant' ? prev.slice(0, -1) : prev;
    });
    setError(null);
    setLoading(true);
    submitAgentMessage(sessionId, lastUserMsg.content, pageContextRef.current)
      .then(({ requestId }) => setCurrentRequestId(requestId))
      .catch(() => {
        setError('Retry failed.');
        setLoading(false);
      });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    handleNewChat();
    setOpen(false);
  };

  const handleNewChat = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setCurrentRequestId(null);
    setSessionId(generateUUID());
    setMessages([]);
    setError(null);
    setLoading(false);
    setActiveView('chat');
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
    } catch {
      // silent — list already reflects optimistic removal
    }
  };

  const handleShowSessions = async () => {
    setActiveView('sessions');
    try {
      const data = await fetchSessions();
      setSessions(data);
    } catch {
      setSessions([]);
    }
  };

  const handleSelectSession = async (selected: AgentSession) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setCurrentRequestId(null);
    setSessionId(selected.sessionId);
    setLoading(false);
    setError(null);

    try {
      const msgs = await fetchSessionMessages(selected.sessionId);
      const chatMessages: ChatMessage[] = [];
      for (const m of msgs) {
        chatMessages.push({ role: 'user', content: m.userMessage });
        if (m.events) {
          const converted = eventsToMessages(m.events as AgentEvent[]);
          chatMessages.push(...converted);
          const hasText = converted.some(c => c.role === 'assistant');
          if (!hasText && m.agentResponse) {
            chatMessages.push({ role: 'assistant', content: m.agentResponse });
          }
        } else if (m.agentResponse) {
          chatMessages.push({ role: 'assistant', content: m.agentResponse });
        }
      }
      setMessages(chatMessages);
    } catch {
      setMessages([]);
    }
    setActiveView('chat');
  };

  const [expandedWidth, setExpandedWidth] = useState(420);
  const isResizing = useRef(false);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    const startX = e.clientX;
    const startWidth = expandedWidth;
    const onMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = Math.max(320, Math.min(window.innerWidth * 0.8, startWidth + (startX - ev.clientX)));
      setExpandedWidth(newWidth);
    };
    const onUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [expandedWidth]);

  const panelSx = viewMode === 'expanded' ? {
    position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1300,
    width: { xs: '100%', sm: expandedWidth }, height: '100%',
    display: open ? 'flex' : 'none', flexDirection: 'column',
    borderRadius: 0, overflow: 'hidden',
    borderLeft: `1px solid ${theme.palette.divider}`,
  } : {
    position: 'fixed', bottom: { xs: 0, sm: 24 }, right: { xs: 0, sm: 24 }, zIndex: 1300,
    width: { xs: '100%', sm: 420 }, height: { xs: '100%', sm: 600 },
    maxHeight: { xs: '100vh', sm: 'calc(100vh - 48px)' },
    display: open ? 'flex' : 'none', flexDirection: 'column',
    borderRadius: { xs: 0, sm: '24px' }, overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  };

  return (
    <>
      {/* FAB — collapsed state */}
      {!open && (
        <Tooltip title="Chat with DocScout AI" placement="left">
          <Fab
            aria-label="Chat with DocScout AI"
            onClick={() => setOpen(true)}
            sx={{
              position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
              width: 60, height: 60,
              background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
              color: theme.palette.primary.contrastText,
              boxShadow: '0 8px 32px rgba(17,28,45,0.3)',
              '&:hover': { background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.light} 100%)` },
            }}
          >
            <AutoAwesome />
          </Fab>
        </Tooltip>
      )}

      {/* Chat panel / sidebar */}
      <Fade in={open}>
        <Paper elevation={24} sx={panelSx as any}>
          {/* Drag handle — expanded mode only */}
          {viewMode === 'expanded' && (
            <Box
              onMouseDown={handleResizeStart}
              sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, zIndex: 1,
                cursor: 'col-resize', '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
              }}
            />
          )}
          {/* Header */}
          <Box sx={{
            px: 2.5, py: 1.5,
            background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
            color: theme.palette.primary.contrastText, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <SmartToy sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>DocScout AI</Typography>
                <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.65rem' }}>Legal Intelligence Active</Typography>
              </Box>
            </Box>
            <Box>
              <Tooltip title={viewMode === 'expanded' ? 'Collapse' : 'Expand sidebar'}>
                <IconButton size="small" onClick={() => setViewMode(viewMode === 'expanded' ? 'panel' : 'expanded')} sx={{ color: 'inherit' }}>
                  {viewMode === 'expanded' ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="New chat">
                <IconButton size="small" onClick={handleNewChat} sx={{ color: 'inherit' }}>
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Chat history">
                <IconButton size="small" onClick={handleShowSessions} sx={{ color: 'inherit' }}>
                  <History fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Minimize">
                <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }}>
                  <Remove fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" onClick={handleClose} sx={{ color: 'inherit' }}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Info banner */}
          <Box sx={{ px: 2, py: 0.5, bgcolor: theme.palette.warning.light, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Warning sx={{ fontSize: 12, color: theme.palette.warning.main }} />
            <Typography variant="caption" sx={{ color: theme.palette.warning.dark, fontSize: '0.6rem' }}>
              Experimental — Use natural language to navigate DocScout and do anything you'd do manually.
            </Typography>
          </Box>

          {/* Session list view */}
          {activeView === 'sessions' ? (
            <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: theme.palette.mode === 'light' ? '#fff' : theme.palette.background.default }}>
              <Box sx={{ p: 2 }}>
                <Button
                  variant="contained" startIcon={<Add />} fullWidth
                  onClick={handleNewChat}
                  sx={{ mb: 2, borderRadius: '12px', textTransform: 'none' }}
                >
                  New Chat
                </Button>
                {sessions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                    No previous sessions
                  </Typography>
                ) : (
                  <List disablePadding>
                    {sessions.map(s => (
                      <ListItemButton
                        key={s.sessionId}
                        onClick={() => handleSelectSession(s)}
                        sx={{ borderRadius: '8px', mb: 0.5 }}
                      >
                        <ListItemText
                          primary={s.title || 'Untitled'}
                          secondary={new Date(s.lastMessageAt).toLocaleDateString()}
                          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleDeleteSession(s.sessionId); }}
                          sx={{ color: theme.palette.text.secondary, ml: 1 }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          ) : (
            <>
              {/* Messages */}
              <Box sx={{
                flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5,
                bgcolor: theme.palette.mode === 'light' ? '#fff' : theme.palette.background.default,
              }}>
                {messages.length === 0 && !loading && (
                  <Box sx={{ textAlign: 'center', mt: 8, px: 3 }}>
                    <SmartToy sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Hi! I can help you create projects, define columns, run analysis, and more. What would you like to do?
                    </Typography>
                  </Box>
                )}

                {messages.map((msg, i) => {
                  if (msg.role === 'user') {
                    return (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Box sx={{
                          maxWidth: '80%', px: 2, py: 1.5, borderRadius: '18px 18px 4px 18px',
                          bgcolor: theme.palette.action.selected, color: theme.palette.text.primary, fontSize: '0.85rem', lineHeight: 1.5,
                          boxShadow: '0 1px 3px rgba(17,28,45,0.08)',
                        }}>
                          {msg.content}
                        </Box>
                      </Box>
                    );
                  }
                  if (msg.role === 'tool') {
                    return (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Chip
                          size="small"
                          label={msg.content}
                          sx={{
                            fontSize: '0.7rem', fontWeight: 600, maxWidth: '85%',
                            bgcolor: msg.toolStatus === 'error'
                              ? alpha(theme.palette.error.main, 0.12)
                              : msg.toolStatus === 'done'
                                ? alpha(theme.palette.success.main, 0.12)
                                : alpha(theme.palette.info.main, 0.12),
                            color: msg.toolStatus === 'error'
                              ? theme.palette.error.dark
                              : msg.toolStatus === 'done'
                                ? theme.palette.success.dark
                                : theme.palette.info.dark,
                          }}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Box sx={{
                        maxWidth: '85%', px: 2, py: 1.5, borderRadius: '18px 18px 18px 4px',
                        bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`,
                        fontSize: '0.85rem', lineHeight: 1.6, color: theme.palette.text.primary,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        '& br:first-of-type': { display: 'none' },
                      }}
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content, mdColors) }}
                      />
                    </Box>
                  );
                })}

                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                    <Box sx={{
                      px: 2, py: 1.5, borderRadius: '18px 18px 18px 4px',
                      bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`,
                      display: 'flex', alignItems: 'center', gap: 1,
                    }}>
                      <CircularProgress size={14} thickness={5} />
                      <Typography variant="caption" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary }}>
                        Thinking...
                      </Typography>
                    </Box>
                  </Box>
                )}

                {error && !loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Button size="small" startIcon={<Replay />} onClick={handleRetry} variant="outlined" sx={{ borderRadius: '12px', textTransform: 'none' }}>
                      Retry
                    </Button>
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </Box>

              {/* Input */}
              <Box sx={{ p: 2, bgcolor: theme.palette.background.paper, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    size="small"
                    placeholder="Ask DocScout AI..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '16px', bgcolor: theme.palette.mode === 'light' ? '#fff' : theme.palette.background.default, fontSize: '0.85rem',
                        '& fieldset': { border: 'none' },
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleSend}
                    disabled={!input.trim() || loading}
                    sx={{
                      width: 40, height: 40, borderRadius: '12px',
                      bgcolor: theme.palette.secondary.dark, color: theme.palette.primary.contrastText,
                      '&:hover': { bgcolor: theme.palette.secondary.main },
                      '&.Mui-disabled': { bgcolor: theme.palette.action.disabledBackground, color: theme.palette.text.secondary },
                    }}
                  >
                    <Send sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Fade>
    </>
  );
};
